import { defineBoot } from '#q-app'
import http from 'axios'
import { LocalStorage } from 'quasar'
import { config as conf } from '../config'
import responser from 'src/api/responser.js'
import { assertNoEmptyIdSegment } from 'src/api/emptyIdRequestGuard.js'
import { installNetworkTracker } from 'src/utils/networkTracker'

// Timeout default del cliente HTTP compartido (WDUR-tabs-blancas): axios NO trae uno propio, y sin
// esto una peticion cuya conexion nunca resuelve (red medio-muerta, o contencion del limite de
// conexiones por origen del navegador con 3+ pestañas abiertas al mismo enlace) se queda pendiente
// PARA SIEMPRE. El caso real que expuso esto: boot/boot.js hace `await usersStore.fetchCurrent()`
// ANTES de boot/offline.js -> con esa peticion colgada, TODO el arranque de la app se cuelga con
// ella (pantalla en blanco permanente, sin error visible). 30s: mismo orden de magnitud que
// WARM_FETCH_CONFIG (org-config.js) y CATALOG_FETCH_CONFIG (tasks/actions.js), que ya cubrian este
// mismo hueco caso por caso. Cualquier request que necesite mas (pulls de catalogo, hasta 300s) ya
// pasa su propio `timeout` explicito por config, que en axios pisa este default.
const DEFAULT_HTTP_TIMEOUT_MS = 30000

// Piso mas generoso para uploads (FormData) Y DESCARGAS (blob/arraybuffer): en XHR el `timeout`
// cubre la request COMPLETA desde send() -- para un upload incluye la SUBIDA del binario, para una
// descarga incluye la BAJADA del body y el tiempo que el backend tarda en GENERARLO. Auditoria
// (review PR #1383 + review demo #201): 15+ sitios de FormData y 30+ de responseType blob/arraybuffer
// (reportes, excel, streams de video, imagenes) y NINGUNO pasa su propio `timeout` -- con el default
// de 30s de arriba, un reporte grande o una foto sobre red de campo lenta abortarian a medio camino.
// En vez de tocar cada call site uno por uno (fragil: cualquier subida/descarga NUEVA se quedaria
// otra vez sin cubrir), el interceptor de abajo sube el piso SOLO para requests que no traigan ya un
// timeout propio mas largo. 120s: mismo orden que DEFAULT_TIMEOUT_MS de offline/sync/transport.js.
// services/reports.js (generacion server-side de PDF/XLSX, tenants grandes) sigue necesitando SU
// PROPIO timeout explicito mas generoso encima de este piso -- ver REPORTS_CONFIG ahi.
const LONG_RESPONSE_TIMEOUT_MS = 120000

// A nivel de modulo, no dentro de defineBoot: `http` es la instancia default global de axios
// (no axios.create()), y 'axios' es el primer boot del arreglo hoy — pero fijar el default aqui
// lo vuelve independiente de ese orden. axios.create() mergea sus defaults desde axios.defaults
// AL MOMENTO de crearse (mergeConfig): si algun modulo importado antes de que este boot corra
// llegara a instanciar su propio axios.create() a nivel de modulo, heredaria este timeout en vez
// de quedar silenciosamente sin uno.
http.defaults.timeout = DEFAULT_HTTP_TIMEOUT_MS

export default defineBoot(async ({ app, router }) => {
  const API_URL = conf('api.url')
  const GRAPHQL_URL = conf('gql.url')

  // Primero en registrarse a proposito: axios corre los interceptores de request en
  // orden inverso, asi que este es el ultimo en correr y ve el config final (con
  // Authorization ya puesto). En response corre primero y ve el status crudo, antes
  // de que responser convierta el error. Ver src/utils/networkTracker.js.
  installNetworkTracker(http)

  http.interceptors.request.use(config => {
    // TKT-2145: cancela peticiones cuya URL trae un id vacío/undefined/null
    // interpolado (ver src/api/emptyIdRequestGuard.js para el detalle y
    // cómo desactivarlo/ajustarlo si genera falsos positivos).
    try {
      assertNoEmptyIdSegment(config)
    } catch (error) {
      console.error(error)
      return Promise.reject(error)
    }

    if (config.url.includes(API_URL) && LocalStorage.getItem('access_token')) {
      config.headers.Authorization = `Bearer ${LocalStorage.getItem('access_token')}`
    }
    if (config.url.includes(GRAPHQL_URL) && LocalStorage.getItem('gql_access_token')) {
      config.headers.Authorization = `Bearer ${LocalStorage.getItem('gql_access_token')}`
    }

    // Uploads/descargas: sube el piso de timeout SOLO si el caller no pidio uno propio mas largo
    // (config.timeout llega aqui ya mergeado con el default de arriba, asi que <= DEFAULT_HTTP_TIMEOUT_MS
    // significa "nadie lo toco, o pidio algo igual o mas corto"). Nunca lo baja: un caller que ya
    // pide mas de 120s (pulls de catalogo, REPORTS_CONFIG) no se ve afectado. `> 0` deja pasar
    // `timeout: 0` (el "sin timeout" explicito de axios): sin este guard seria el UNICO opt-out real
    // que le queda a un caller tras el default global, y este `<=` se lo comeria de vuelta.
    const isUpload = config.data instanceof FormData
    const isDownload = config.responseType === 'blob' || config.responseType === 'arraybuffer'
    if ((isUpload || isDownload) && config.timeout > 0 && config.timeout <= DEFAULT_HTTP_TIMEOUT_MS) {
      config.timeout = LONG_RESPONSE_TIMEOUT_MS
    }

    return config
  })

  http.interceptors.response.use(
    response => responser(response, router),
    error => responser(error, router)
  )

  app.config.globalProperties.$axios = http
})

export { http }
